import './index.css'

import cn from 'clsx'
import React, { Component } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { Link, NavigateFunction, useNavigate } from 'react-router-dom'

import { TRACK_NESTING } from '@disco/constants/FeatureTypes'
import { CUSTOM_CHANNEL, INBOX_TYPES } from '@disco/constants/GroupTypes'
import { BRIEF, PLAYLIST, TRACK } from '@disco/constants/ItemTypes'
import { PLAYLIST_PANE } from '@disco/constants/PaneTypes'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { toggleShortlists } from '@disco/state-management/actions/AppActions'
import { setCopyMetadata } from '@disco/state-management/actions/copy-metadata-actions'
import {
  addTracksToLibrary,
  alterPlaylistGroups,
  alterTrackGroups,
  removeTracksFromPlaylist,
} from '@disco/state-management/actions/ExplorerActions'
import { resetSelection } from '@disco/state-management/actions/SelectionActions'
import {
  bulkDeletePlaylists,
  bulkDeleteTracks,
} from '@disco/state-management/actions/ServerActions'
import {
  createNewShortlist,
  insertSelectedItems,
} from '@disco/state-management/actions/ShortlistActions'
import {
  getSelectedLibrary,
  getShortlistsOpen,
} from '@disco/state-management/selectors/app'
import { isFeatureEnabled } from '@disco/state-management/selectors/business'
import { makeGetGroupLocks } from '@disco/state-management/selectors/groups'
import {
  getExplorerSelectedItems,
  getExplorerSelectedPane,
  getExplorerSelectedPlaylists,
  getExplorerSelectedTracks,
  getExplorerSelectionGroup,
} from '@disco/state-management/selectors/selection'
import {
  getFirstVisibleShortlistId,
  getVisibleShortlistCount,
} from '@disco/state-management/selectors/shortlists'
import {
  getConstanceSettings,
  getHideChannelRemovalSetting,
  getHideInboxRemovalSetting,
} from '@disco/state-management/selectors/user'
import { Brief } from '@disco/types/types/brief'
import { SelectableItem } from '@disco/types/types/selection'
import {
  sendPlaylistDownloadEventThrottled,
  sendTrackDownloadEventThrottled,
} from '@disco/utils/eventsApi'
import { groupUrlsContainGroupIds } from '@disco/utils/groups'
import { createNestedSetLocation } from '@disco/utils/nesting'
import { selectionContainNestedTracks } from '@disco/utils/selection'
import { areTrackTypesMixed } from '@disco/utils/tracks'
import { generateMetadataCsv } from 'utils/csv-metadata-export'
import { getTrackRightsSplits } from '@disco/api/rights-data'
import { getVisibleCustomFieldsKeys } from '@disco/state-management/selectors/user'
import { getTracks } from '@disco/state-management/selectors/tracks'
import {
  getContactsList,
  getPros,
  getPublishers,
} from '@disco/state-management/selectors/contacts'
import { loadPlaylistZipUrls } from '@disco/state-management/actions/playlist-actions'
import { fetchTrackMetadata } from '@disco/state-management/actions/ServerActions'
import { loadContactsByIds } from '@disco/state-management/actions/contact-actions'
import type { Contact } from 'types/list'
import type { Pro, Publisher } from 'types/writer'
import type { Track, TrackId } from 'types/track'

import type { ExplorerPane } from 'types/explorer'
import type { Group } from 'types/group'
import type { Playlist } from 'types/playlist'

import enhanceWithClickOutside from 'utils/enhance-with-click-outside'

import { Flex, Text } from 'components/ds'
import ContextMenu from 'components/common/context-menu'
import { getExplorerEnabledClasses } from 'components/explorer/action-menu/menu-state'
import BetaBadge from 'components/ui/beta-badge'
import Button from 'components/ui/button'
import Tooltip from 'components/ui/tooltip'

import ExplorerActionMenuContainer from './explorer-action-menu-container'

function mapStateToProps(state: any) {
  const selectedItems = getExplorerSelectedItems(state)
  const selectedTracks = getExplorerSelectedTracks(state)
  const selectedLibrary = getSelectedLibrary(state)
  const getGroupLocks = makeGetGroupLocks()
  const nestingEnabled = isFeatureEnabled(state, {
    featureName: TRACK_NESTING,
  })
  return {
    selectedPane: getExplorerSelectedPane(state),
    selectedLibrary,
    selectionGroup: getExplorerSelectionGroup(state),
    firstShortlistId: getFirstVisibleShortlistId(state),
    shortlistsCount: getVisibleShortlistCount(state),
    selectedItems: selectedItems as SelectableItem[],
    selectedTracks,
    selectedTracksWithinUserTerritories: selectedTracks.filter(
      (track: Track) => !track.outside_your_territories,
    ),
    selectedPlaylists: getExplorerSelectedPlaylists(state),
    hideInboxRemoval: getHideInboxRemovalSetting(state),
    hideChannelRemoval: getHideChannelRemovalSetting(state),
    lockContent:
      (selectedLibrary &&
        getGroupLocks(state, {
          groupId: selectedLibrary.id,
        })?.lock_content) ??
      false,
    nestingEnabled,
    isPlaylistCreatorOpen: getShortlistsOpen(state),
    tracks: getTracks(state),
    contacts: getContactsList(state),
    pros: getPros(state),
    publishers: getPublishers(state),
    customFieldsKeys: getVisibleCustomFieldsKeys(state),
    downloadPlaylistMetadataCsvEnabled:
      !!getConstanceSettings(state)?.[
        'PLT-448-DOWNLOAD-PLAYLIST-METADATA-CSV-ROLLOUT'
      ],
  }
}

const mapDispatchToProps = {
  createNewShortlist,
  insertSelectedItems,
  addTracksToLibrary,
  bulkDeletePlaylists,
  bulkDeleteTracks,
  alterTrackGroups,
  alterPlaylistGroups,
  removeTracksFromPlaylist,
  resetSelection,
  sendMixpanelEvent,
  setCopyMetadata,
  toggleShortlists,
  fetchTrackMetadata,
  loadContactsByIds,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type PropsFromRedux = ConnectedProps<typeof connector>

const itemClass = disabled =>
  cn('context-menu__option', {
    'is-disabled': disabled,
  })

type OwnState = {
  dropdownOpen: boolean
}
type OwnProps = {
  navigate: NavigateFunction
  basePath?: string
}

type Props = PropsFromRedux & OwnProps

class ExplorerActionMenu extends Component<Props, OwnState> {
  state = {
    dropdownOpen: false,
  }
  _handleDropdownToggle = () => {
    if (this.props.selectedItems.length) {
      this.setState({
        dropdownOpen: !this.state.dropdownOpen,
      })
    }
  }
  _handlePlaylistsOpen = closeMenu => {
    // TODO: prevent opening too many playlists
    this.props.selectedPlaylists.forEach(playlist =>
      this.props.createNewShortlist(playlist),
    )

    if (!this.props.isPlaylistCreatorOpen) {
      const isSearchPage = Boolean(window.location.pathname.includes('/search'))
      const currentLocation = isSearchPage ? 'Search' : 'Explorer'
      this.props.toggleShortlists(true, currentLocation, 'Edit Playlist')
    }

    closeMenu()
  }

  handleClickOutside() {
    this.setState({
      dropdownOpen: false,
    })
  }

  _getPlaylistId() {
    return this.props.selectedPane?.playlist_id
  }

  _sendDownloadEvents(items: SelectableItem[]) {
    const { selectedLibrary } = this.props
    if (!selectedLibrary) return
    items.forEach(item => {
      if (item.type === TRACK) {
        sendTrackDownloadEventThrottled(item.id, 'in-app', {
          group_id: selectedLibrary.id,
          playlist_id: this._getPlaylistId(),
        })
      } else if (item.type === PLAYLIST) {
        sendPlaylistDownloadEventThrottled(
          item.playlist_id,
          undefined,
          selectedLibrary.id,
        )
      }
    })
  }

  _handleAddToShortlist = closeMenu => {
    const { firstShortlistId, selectedItems } = this.props

    if (
      this.props.selectedLibrary &&
      INBOX_TYPES.includes(this.props.selectedLibrary.group_type)
    ) {
      this._sendDownloadEvents(selectedItems)
    }

    // Invariant: this handler only runs when shortlistsCount === 1, so the
    // single visible shortlist's id is always present.
    this.props.insertSelectedItems(firstShortlistId!, selectedItems)
    this.props.resetSelection()
    closeMenu()
  }
  _handleAddToLibrary = closeMenu => {
    const { selectedTracksWithinUserTerritories } = this.props
    this.props.addTracksToLibrary(selectedTracksWithinUserTerritories)

    this._sendDownloadEvents(selectedTracksWithinUserTerritories)

    this.props.sendMixpanelEvent('Inbox accept multiple tracks')
    this.props.resetSelection()
    closeMenu()
  }
  _handleRemoveFromChannel = closeMenu => {
    const {
      selectedTracksWithinUserTerritories,
      selectedPlaylists,
      selectionGroup,
      hideChannelRemoval,
    } = this.props
    const tracksToDelete = (
      selectedTracksWithinUserTerritories as Track[]
    ).filter(
      t =>
        t.groups &&
        selectionGroup &&
        selectionGroup.group_type === CUSTOM_CHANNEL &&
        groupUrlsContainGroupIds(t.groups, [selectionGroup.id]),
    )
    const playlistsToDelete = (selectedPlaylists as Playlist[]).filter(
      t =>
        t.groups &&
        selectionGroup &&
        selectionGroup.group_type === CUSTOM_CHANNEL &&
        groupUrlsContainGroupIds(t.groups, [selectionGroup.id]),
    )

    if (
      (tracksToDelete.length || playlistsToDelete.length) &&
      !hideChannelRemoval
    ) {
      this.props.navigate(
        `${this.props.basePath ?? ''}/selection/remove-from-channel`,
      )
    } else {
      if (tracksToDelete.length && selectionGroup) {
        this.props.alterTrackGroups(tracksToDelete, [], [selectionGroup])
      }

      if (playlistsToDelete.length && selectionGroup) {
        this.props.alterPlaylistGroups(playlistsToDelete, [], [selectionGroup])
      }

      this.props.resetSelection()
    }

    closeMenu()
  }
  _handleRemoveFromInbox = closeMenu => {
    const {
      selectedTracksWithinUserTerritories,
      selectedPlaylists,
      selectedLibrary,
      hideInboxRemoval,
    } = this.props
    const tracksToRemove = (
      selectedTracksWithinUserTerritories as Track[]
    ).filter(t => t.groups && t.groups.length > 1)
    const tracksToDelete = (
      selectedTracksWithinUserTerritories as Track[]
    ).filter(
      t =>
        t.groups &&
        t.groups.length === 1 &&
        selectedLibrary &&
        selectedLibrary.url === t.groups[0],
    )
    const playlistsToRemove = (selectedPlaylists as Playlist[]).filter(
      t => t.groups && t.groups.length > 1,
    )
    const playlistsToDelete = (selectedPlaylists as Playlist[]).filter(
      t =>
        t.groups &&
        t.groups.length === 1 &&
        selectedLibrary &&
        selectedLibrary.url === t.groups[0],
    )

    if (
      selectedLibrary &&
      (tracksToDelete.length || playlistsToDelete.length) &&
      !hideInboxRemoval
    ) {
      this.props.navigate(`/inboxes/${selectedLibrary.id}/selection/remove`)
    } else {
      if (playlistsToDelete.length) {
        this.props.bulkDeletePlaylists(
          playlistsToDelete.map(p => p.playlist_id),
        )
      }

      if (tracksToDelete.length) {
        this.props.bulkDeleteTracks(
          tracksToDelete.map(t => t.id),
          'Remove from DISCO action inside inbox playlist',
          null,
        )
      }

      if (playlistsToRemove.length && selectedLibrary) {
        this.props.alterPlaylistGroups(playlistsToRemove, [], [selectedLibrary])
      }

      if (tracksToRemove.length) {
        if (
          this.props.selectedPane?.type === PLAYLIST_PANE &&
          this.props.selectedPane?.playlist_id
        ) {
          this.props.removeTracksFromPlaylist(
            {
              playlist_id: this.props.selectedPane?.playlist_id,
            },
            tracksToRemove,
          )
        }
      }

      if (tracksToRemove.length || tracksToDelete.length) {
        this.props.sendMixpanelEvent('Inbox reject multiple tracks')
      }

      if (playlistsToRemove.length || playlistsToDelete.length) {
        this.props.sendMixpanelEvent('Inbox remove multiple playlists')
      }

      this.props.resetSelection()
    }

    closeMenu()
  }

  _handleDownloadMetadataCsv = async closeMenu => {
    const { selectedTracks, tracks, contacts, pros, publishers, customFieldsKeys } =
      this.props

    const { fetchTrackMetadata } = this.props

    closeMenu()

    if (selectedTracks.length > 0) {
      await generateMetadataCsv({
        manualTracks: selectedTracks as Track[],
        data: {
          tracks,
          contacts,
          pros,
          publishers,
          customFieldsKeys,
        },
        actions: {
          fetchTrackMetadata,
          fetchTrackRightsSplits: trackId => getTrackRightsSplits(trackId, 'publishing'),
        },
      })
    }
  }

  renderAddToShortlistAction(className, closeMenu) {
    if (this.props.shortlistsCount === 1) {
      return (
        <a
          className={className}
          onClick={() => this._handleAddToShortlist(closeMenu)}
        >
          Add to Playlist Creator
        </a>
      )
    }

    const selectionBase = `${this.props.basePath ?? ''}/selection`
    return (
      <Link
        className={className}
        to={`${selectionBase}/add-to-shortlist${
          this.props.selectedLibrary
            ? `?group=${this.props.selectedLibrary.id}`
            : ''
        }`}
        onClick={closeMenu}
      >
        Add to Playlist Creator
      </Link>
    )
  }

  _getEnabledClasses() {
    const {
      selectedItems,
      selectedPlaylists,
      selectedTracks,
      selectedTracksWithinUserTerritories,
      selectedLibrary,
      selectedPane,
      selectionGroup,
      lockContent,
    } = this.props
    return getExplorerEnabledClasses({
      selectedItems: selectedItems as SelectableItem[],
      selectedPlaylists,
      selectedTracks,
      selectedTracksWithinUserTerritories,
      selectedLibrary,
      selectedPane,
      selectionGroup,
      lockContent,
    })
  }

  _getTooltipMessage(message?: string) {
    const { selectedItems } = this.props
    const nestedTracksHaveSelected = selectionContainNestedTracks(selectedItems)
    return nestedTracksHaveSelected
      ? 'This function is not supported for nested versions'
      : message
  }

  _getSelectedStatus() {
    const { selectedItems } = this.props
    const items = selectedItems as SelectableItem[]
    const onlyTracksSelected = items.every(
      item => 'type' in item && item.type === TRACK,
    )
    const onlyPlaylistsSelected = items.every(
      item => 'type' in item && item.type === PLAYLIST,
    )

    const selectionContainsBriefs = items.some(
      item => 'type' in item && item.type === BRIEF,
    )

    return {
      onlyTracksSelected,
      onlyPlaylistsSelected,
      selectionContainsBriefs,
    }
  }

  _renderDropdown({ closeMenu }: { closeMenu: () => void }) {
    const {
      selectedTracks,
      selectedLibrary,
      nestingEnabled,
      selectedPane,
      basePath,
      downloadPlaylistMetadataCsvEnabled,
    } = this.props
    const selectionBase = `${basePath ?? ''}/selection`
    const copyMetadataPath = basePath
      ? `${basePath}/copy-metadata`
      : '/explorer/copy-metadata'

    const { onlyPlaylistsSelected, onlyTracksSelected } =
      this._getSelectedStatus()

    const inboxActionsShown =
      selectedLibrary && INBOX_TYPES.indexOf(selectedLibrary.group_type) > -1

    const {
      channelActionEnabledClass,
      copyMetadataActionEnabledClass,
      deleteActionEnabledClass,
      mixedActionEnabledClass,
      removeFromChannelClass,
      inboxMixedActionEnabledClass,
      inboxTrackActionEnabledClass,
      manageNestingClass,
    } = this._getEnabledClasses()

    // TODO: clean-up
    // const dropdownClassName = cn('ExplorerActions_Menu_DropdownMenu', {
    //   'is-open': this.state.dropdownOpen,
    // })
    const tooltipMessage = this._getTooltipMessage()

    return (
      <React.Fragment>
        {inboxActionsShown && (
          <Tooltip content={tooltipMessage}>
            <a
              className={inboxTrackActionEnabledClass}
              onClick={() => this._handleAddToLibrary(closeMenu)}
            >
              Add to library
            </a>
          </Tooltip>
        )}
        {inboxActionsShown && (
          <Tooltip content={tooltipMessage}>
            <a
              className={inboxMixedActionEnabledClass}
              onClick={() => this._handleRemoveFromInbox(closeMenu)}
            >
              Remove from inbox
            </a>
          </Tooltip>
        )}
        <Tooltip content={tooltipMessage}>
          <Link
            className={channelActionEnabledClass}
            to={`${selectionBase}/add-to-channel`}
            onClick={closeMenu}
          >
            Add to channel
          </Link>
        </Tooltip>
        <Tooltip content={tooltipMessage}>
          <a
            className={removeFromChannelClass}
            onClick={() => this._handleRemoveFromChannel(closeMenu)}
          >
            Remove from channel
          </a>
        </Tooltip>

        {nestingEnabled && (
          <Link
            // Invariant: nesting is only enabled for a selection with an audio
            // main track, so the location (and its required `to`) is present.
            {...createNestedSetLocation(selectedTracks)!}
            className={manageNestingClass}
            onClick={closeMenu}
          >
            Manage nesting
          </Link>
        )}

        {onlyTracksSelected && (
          <Tooltip content={tooltipMessage}>
            <Link
              className={copyMetadataActionEnabledClass}
              to={copyMetadataPath}
              onClick={() => {
                const trackIds = (selectedTracks as Track[]).map(
                  track => track.id,
                )
                this.props.setCopyMetadata({
                  targetTrackIds: trackIds,
                })
                this.props.sendMixpanelEvent(
                  'Copy track info to these tracks (menu)',
                )
                closeMenu()
              }}
            >
              Copy track info to these tracks
            </Link>
          </Tooltip>
        )}
        {onlyTracksSelected &&
          selectedPane?.type === PLAYLIST_PANE &&
          downloadPlaylistMetadataCsvEnabled && (
            <Tooltip content={tooltipMessage}>
              <a
                className={mixedActionEnabledClass}
                onClick={() => this._handleDownloadMetadataCsv(closeMenu)}
              >
                <Flex align="center" gap="2">
                  <Text size="2">Download Metadata (CSV)</Text>
                  <BetaBadge />
                </Flex>
              </a>
            </Tooltip>
          )}
        <Tooltip content={tooltipMessage}>
          {this.renderAddToShortlistAction(mixedActionEnabledClass, closeMenu)}
        </Tooltip>

        <Link
          className={deleteActionEnabledClass}
          to={`${selectionBase}/delete`}
          onClick={closeMenu}
        >
          Delete from DISCO
        </Link>
      </React.Fragment>
    )
  }

  render() {
    const {
      deleteActionEnabledClass,
      trackActionEnabledClass,
      mergeEnabledClass,
    } = this._getEnabledClasses()

    const { selectedItems, selectedTracks } = this.props

    if (selectedItems.length < 2) {
      return null
    }

    const {
      onlyTracksSelected,
      onlyPlaylistsSelected,
      selectionContainsBriefs,
    } = this._getSelectedStatus()

    // Do not show actions for briefs
    if (selectionContainsBriefs) {
      return null
    }

    const selectionBase = `${this.props.basePath ?? ''}/selection`
    const mergeLink = areTrackTypesMixed(selectedTracks)
      ? `${selectionBase}/merge-disabled`
      : `${selectionBase}/merge`
    let editButton = (
      <Button
        key="edit-large"
        appearance="icon"
        compact
        icon="edit-large"
        tooltip={this._getTooltipMessage('Edit selected')}
        disabled={trackActionEnabledClass.includes('disabled')}
        link={`${selectionBase}/bulk-edit`}
      />
    )

    if (onlyPlaylistsSelected) {
      editButton = (
        <Button
          key="edit-large"
          appearance="icon"
          compact
          icon="edit-large"
          tooltip={this._getTooltipMessage('Edit selected')}
          onClick={() => this._handlePlaylistsOpen(() => {})}
        />
      )
    }

    return (
      <ExplorerActionMenuContainer
        selectedItems={this.props.selectedItems as SelectableItem[]}
        buttons={[
          editButton,
          onlyTracksSelected && (
            <Button
              key="merge"
              appearance="icon"
              compact
              icon="merge"
              tooltip={this._getTooltipMessage('Merge')}
              disabled={mergeEnabledClass.includes('disabled')}
              link={mergeLink}
            />
          ),
          <Button
            key="delete-bucket"
            appearance="icon"
            compact
            icon="delete-bucket"
            tooltip="Delete selected"
            disabled={deleteActionEnabledClass.includes('disabled')}
            link={`${selectionBase}/delete`}
          />,
        ]}
      >
        <div>
          <ContextMenu
            menuClassName="ExplorerActionMenu_Modal"
            getMenuOffset={() => ['-194px', 'calc(-100% - 22px)']}
            appearance="action-menu"
            buttonProps={{
              split: undefined,
              appearance: 'icon',
              compact: true,
              icon: 'ellipsis',
              caption: '',
            }}
          >
            {props => this._renderDropdown(props as { closeMenu: () => void })}
          </ContextMenu>
        </div>
      </ExplorerActionMenuContainer>
    )
  }
}

const enhancedMenu = enhanceWithClickOutside(ExplorerActionMenu)
const ConnectedExplorerActionMenu = connector(enhancedMenu)

const ExplorerActionMenuWithNavigate = (props: any) => {
  const navigate = useNavigate()
  return <ConnectedExplorerActionMenu {...props} navigate={navigate} />
}

export default ExplorerActionMenuWithNavigate
